import { ExpectedError } from "../../expectedError";
import { customAuth } from "../standardAuthPatterns";
import { caCertificateItem, defineConnector } from "../types";
import logo from "./logo.svg";

const DEFAULT_PORT = "1433";
const DEFAULT_DATABASE = "master";
/// The driver requires a syntactically-present password, but pony
/// authenticates with the upstream itself — whatever is sent here is dropped.
const DUMMY_PASSWORD = "tines-proxy";

function hostComponent(host: string | undefined) {
  const value = (host ?? "").trim();
  if (/^\[[0-9a-fA-F:.]+\]$/.test(value)) return value;
  if (!/^[A-Za-z0-9.-]+$/.test(value)) {
    throw new ExpectedError("Invalid MSSQL host");
  }
  return value;
}

function portComponent(port: string | undefined) {
  const value = (port || DEFAULT_PORT).trim();
  if (!/^\d+$/.test(value)) throw new ExpectedError("Invalid MSSQL port");
  const portNumber = Number(value);
  if (portNumber < 1 || portNumber > 65535) {
    throw new ExpectedError("Invalid MSSQL port");
  }
  return value;
}

function databaseComponent(database: string | undefined) {
  const value = database?.trim() || DEFAULT_DATABASE;
  if (/[;'"=]/.test(value)) {
    throw new ExpectedError("Invalid MSSQL database");
  }
  return value;
}

function usernameComponent(username: string | undefined) {
  const value = (username ?? "").trim();
  if (/[;'"=]/.test(value)) {
    throw new ExpectedError("Invalid MSSQL username");
  }
  return value;
}

function connectionString({
  host,
  port,
  username,
  database,
}: Record<string, string>) {
  const mssqlHost = hostComponent(host);
  const mssqlPort = portComponent(port);
  const mssqlDatabase = databaseComponent(database);
  const mssqlUsername = usernameComponent(username);
  const parts = [
    `Server=tcp:${mssqlHost},${mssqlPort}`,
    `Database=${mssqlDatabase}`,
    `User ID=${mssqlUsername}`,
    `Password=${DUMMY_PASSWORD}`,
    `Encrypt=False`,
    `TrustServerCertificate=True`,
  ];
  return parts.join(";") + ";";
}

export const mssql = defineConnector({
  identifier: "mssql",
  name: "Microsoft SQL Server",
  tagLine: "Microsoft relational database",
  keywords: "mssql sqlserver tsql azure sql relational rdbms database",
  logo,
  tags: ["data"],
  protocol: "mssql",
  connectionTest: { protocol: "mssql", connectionString },
  defaultUrlPattern: () => "",
  customizeStepHandledInline: true,
  auth: customAuth("mssql"),
  steps: [
    {
      title: "Enter your Microsoft SQL Server connection",
      items: [
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: "MSSQL",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "input",
          label: "Host",
          required: true,
          placeholder: "db.example.com",
          target: { kind: "configuration", field: "host" },
        },
        {
          kind: "input",
          label: "Port",
          required: true,
          defaultValue: "1433",
          target: { kind: "configuration", field: "port" },
        },
        {
          kind: "input",
          label: "Database",
          required: true,
          defaultValue: "master",
          target: { kind: "configuration", field: "database" },
        },
        {
          kind: "input",
          label: "Username",
          required: true,
          target: { kind: "configuration", field: "username" },
        },
        {
          kind: "input",
          label: "Password",
          required: true,
          target: { kind: "secrets", field: "password" },
        },
        {
          ...caCertificateItem,
          target: { kind: "configuration", field: "tlsCaCertificate" },
        },
      ],
    },
  ],
  publicEnvVars: (configuration) => ({
    MSSQL_URL: connectionString(configuration),
  }),
  usageHint: () =>
    "Use the MSSQL_URL environment variable as the SQL Server connection string. The proxy authenticates with the database itself; the password embedded in MSSQL_URL is a placeholder and not your real credential.",
});
